import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { AuthGate } from "@/components/AuthGate";
import { useState, useRef } from "react";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { createReview, generateReview } from "@/lib/reviews.functions";
import { extractDocument } from "@/lib/extract.functions";
import { splitFullDocument } from "@/lib/document-split.functions";
import { ACCEPT_ATTR, classifyUpload } from "@/lib/upload-policy";
import {
  getMyProfile,
  setMyInstitution,
  updateMyAcademicContext,
} from "@/lib/institutions.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ProcessingIndicator } from "@/components/ProcessingIndicator";
import { InstitutionPicker, type InstitutionValue } from "@/components/InstitutionPicker";
import { STARTER_BY_ID, STARTER_IDS, type StarterId } from "@/lib/starters";
import { PageContainer } from "@/components/PageContainer";
import { Eyebrow, DisplayHeading, SectionRule, CTAButton } from "@/components/editorial";
import { ChevronDown, ChevronRight, FileUp, CheckCircle2 } from "lucide-react";

const search = z.object({ starter: z.enum(STARTER_IDS).optional() });

export const Route = createFileRoute("/reviews/new")({
  head: () => ({
    meta: [
      { title: "Start a new review — Tiger's Eye" },
      {
        name: "description",
        content:
          "Drop your draft, confirm a couple of details, and get an impartial Tiger's Eye critique in minutes.",
      },
      { name: "robots", content: "noindex,follow" },
    ],
  }),
  validateSearch: (s) => search.parse(s),
  component: () => (
    <AuthGate>
      <NewReview />
    </AuthGate>
  ),
});

const LEVELS = [
  "UG Year 1",
  "UG Year 2",
  "UG Year 3",
  "UG Year 4",
  "MA / MSc",
  "PhD",
  "Other",
];

const REFERENCING_STYLES = [
  "Harvard",
  "APA",
  "MLA",
  "Chicago",
  "OSCOLA",
  "Vancouver",
  "IEEE",
  "Other",
];

function NewReview() {
  const navigate = useNavigate();
  const { starter } = Route.useSearch();
  const starterPreset = starter ? STARTER_BY_ID[starter as StarterId] ?? null : null;

  // Server fns
  const profileFn = useServerFn(getMyProfile);
  const createFn = useServerFn(createReview);
  const genFn = useServerFn(generateReview);
  const extractFn = useServerFn(extractDocument);
  const splitFn = useServerFn(splitFullDocument);
  const setInstitutionFn = useServerFn(setMyInstitution);
  const updateAcademicFn = useServerFn(updateMyAcademicContext);

  // Core state — minimum needed to generate.
  const [institution, setInstitution] = useState<InstitutionValue>(null);
  const [mainInstitutionName, setMainInstitutionName] = useState<string>("");
  const [preferredName, setPreferredName] = useState<string>("");
  const [displayNameFallback, setDisplayNameFallback] = useState<string>("");
  const [level, setLevel] = useState<string>("");
  const [referencing, setReferencing] = useState<string>("Harvard");

  // Document state — file processed silently in the background.
  const [filename, setFilename] = useState<string>("");
  const [brief, setBrief] = useState<string>("");
  const [draft, setDraft] = useState<string>("");
  const [processing, setProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);

  // Optional context — collapsed by default.
  const [showMore, setShowMore] = useState(false);
  const [course, setCourse] = useState("");
  const [module, setModule] = useState("");
  const [assessmentType, setAssessmentType] = useState("");
  const [wordCount, setWordCount] = useState("");
  const [rubric, setRubric] = useState("");

  // Defaults — adjustable from the result page later, not gated here.
  const reviewMode = starterPreset?.reviewMode ?? "full_critique";
  const audience = starterPreset?.audience ?? "student";
  const checkBib = starterPreset?.checkBibliography ?? true;
  const appliedStarter: StarterId | null = starterPreset?.id ?? null;

  // Single merged consent.
  const [consented, setConsented] = useState(false);

  // Prefill from profile (institution, name, prior course/year).
  useQuery({
    queryKey: ["my-profile"],
    queryFn: async () => {
      const res = await profileFn();
      const p = res.profile;
      if (p) {
        if (!institution && p.institution_name) {
          setInstitution(
            p.institution_id
              ? { kind: "registry", id: p.institution_id, name: p.institution_name }
              : { kind: "free", name: p.institution_name },
          );
        }
        if (p.institution_name) setMainInstitutionName(p.institution_name);
        if (p.preferred_name) setPreferredName(p.preferred_name);
        if (p.display_name) setDisplayNameFallback(p.display_name);
        if (!course && p.course) setCourse(p.course);
        if (!level && p.year_of_study) setLevel(p.year_of_study);
      }
      return res;
    },
  });

  // ───────── File handling (silent split, no preview gate) ─────────
  const handleFile = async (file: File) => {
    try {
      classifyUpload(file);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "File not allowed");
      return;
    }
    setProcessing(true);
    setFilename(file.name);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const extracted = await extractFn({ data: fd });
      if (!extracted.text?.trim()) {
        throw new Error("We couldn't read any text from that file.");
      }
      // Try to split silently; if the model fails or returns no brief,
      // fall back to treating the whole file as the draft.
      try {
        const split = await splitFn({ data: { text: extracted.text } });
        setBrief(split.assignment_brief ?? "");
        setDraft(split.student_draft || extracted.text);
      } catch {
        setBrief("");
        setDraft(extracted.text);
      }
      toast.success(`Loaded ${extracted.filename}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to read file");
      setFilename("");
    } finally {
      setProcessing(false);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  // ───────── Submit ─────────
  const submit = useMutation({
    mutationFn: async () => {
      // Persist context to profile (best-effort).
      try {
        await Promise.all([
          updateAcademicFn({
            data: { course: course || undefined, year_of_study: level || undefined },
          }),
          institution && !mainInstitutionName
            ? setInstitutionFn({
                data:
                  institution.kind === "registry"
                    ? { institutionId: institution.id }
                    : { freeText: institution.name },
              }).catch(() => {})
            : Promise.resolve(),
        ]);
      } catch (e) {
        console.warn("Failed to save profile context", e);
      }

      const nameSnapshot =
        preferredName?.trim() || displayNameFallback?.trim() || "Unnamed user";

      const { id } = await createFn({
        data: {
          intake: {
            university: institution?.name ?? "",
            course,
            module,
            year_of_study: level,
            assessment_type: assessmentType,
            word_count: wordCount,
            referencing_style: referencing,
            assignment_brief: brief,
            marking_rubric: rubric,
            review_mode: reviewMode,
            feedback_audience: audience,
            check_bibliography: checkBib,
            ...(appliedStarter
              ? {
                  starter_id: appliedStarter,
                  focus_hint: STARTER_BY_ID[appliedStarter].focusHint,
                }
              : {}),
          },
          draftText: draft,
          reviewContext: {
            institutionId: institution?.kind === "registry" ? institution.id : null,
            institutionName: institution?.name ?? null,
            course: course || null,
            yearOfStudy: level || null,
          },
          attestation: {
            confirmed: true,
            nameSnapshot,
            institutionSnapshot: institution?.name ?? "",
          },
        },
      });
      return id;
    },
    onSuccess: async (id) => {
      navigate({ to: "/reviews/$reviewId", params: { reviewId: id } });
      genFn({ data: { id } }).catch((e: Error) => toast.error(e.message));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const canSubmit =
    !!institution?.name &&
    !!level &&
    draft.trim().length >= 50 &&
    consented &&
    !processing &&
    !submit.isPending;

  return (
    <PageContainer>
      <Eyebrow className="mb-3">New review</Eyebrow>
      <DisplayHeading level={1} balance className="mb-3">
        Start a new review
      </DisplayHeading>
      <p className="text-ink-secondary mb-10 max-w-2xl">
        Drop your draft, confirm a couple of details, and we'll do the rest. Most
        reviews are ready in under two minutes.
      </p>

      {appliedStarter && (
        <div className="mb-8 flex flex-wrap items-center gap-3 border border-primary/40 bg-primary/5 px-4 py-3 text-sm">
          <span className="font-mono text-xs tracking-widest text-primary uppercase">
            Starter applied
          </span>
          <span className="text-ink-secondary">
            {STARTER_BY_ID[appliedStarter].label}
          </span>
        </div>
      )}

      {/* ───────── Zone A: Drop your draft ───────── */}
      <section className="mb-10">
        <Eyebrow className="mb-2">1 · Your draft</Eyebrow>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
          }}
          className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
            dragOver
              ? "border-primary bg-primary/10"
              : draft
                ? "border-primary/50 bg-primary/5"
                : "border-border bg-paper/40 hover:border-primary/50"
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={ACCEPT_ATTR}
            disabled={processing}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = "";
            }}
          />

          {processing ? (
            <ProcessingIndicator
              label="Reading your file"
              estimatedSeconds={15}
              stages={["Extracting text…", "Separating question from answer…", "Almost ready…"]}
            />
          ) : draft ? (
            <div className="space-y-1 min-w-0">
              <CheckCircle2 className="h-8 w-8 mx-auto text-primary" />
              <p className="font-serif text-lg text-primary break-all">{filename || "Draft ready"}</p>
              <p className="text-xs text-ink-tertiary">
                {draft.length.toLocaleString()} characters
                {brief ? ` · assignment question detected` : ""} ·{" "}
                <span className="underline">Replace file</span>
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <FileUp className="h-8 w-8 mx-auto text-ink-tertiary" />
              <p className="font-serif text-lg text-primary">
                Drop your draft here, or click to browse
              </p>
              <p className="text-xs text-ink-tertiary">
                .pdf, .docx, .txt, .md — up to 20 MB. If your file contains both the
                assignment question and your answer, we'll split them automatically.
              </p>
            </div>
          )}
        </div>

        {/* Manual paste fallback — collapsed, no character-count theatre. */}
        <details className="mt-3">
          <summary className="text-xs text-ink-tertiary cursor-pointer hover:text-primary">
            No file? Paste your draft instead.
          </summary>
          <div className="mt-3 space-y-3">
            <Textarea
              rows={10}
              placeholder="Paste your draft here…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="font-mono text-sm"
            />
            <Textarea
              rows={3}
              placeholder="Optional: paste the assignment question / brief here."
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
            />
          </div>
        </details>
      </section>

      {/* ───────── Zone B: Tell us just enough ───────── */}
      <section className="mb-10">
        <Eyebrow className="mb-2">2 · A couple of details</Eyebrow>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="sm:col-span-3">
            <Label>Institution</Label>
            {mainInstitutionName && institution?.name === mainInstitutionName ? (
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border border-border bg-card px-3 py-2 mt-1">
                <div className="min-w-0">
                  <p className="text-sm text-primary font-serif break-words">{mainInstitutionName}</p>
                  <p className="text-xs text-ink-tertiary font-mono tracking-wider">
                    YOUR MAIN INSTITUTION
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setInstitution(null)}
                  className="text-xs font-mono tracking-wider text-primary hover:underline shrink-0"
                >
                  Different for this work?
                </button>
              </div>
            ) : (
              <InstitutionPicker value={institution} onChange={setInstitution} />
            )}
          </div>
          <div>
            <Label htmlFor="level">Level</Label>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger id="level" aria-label="Level of study">
                <SelectValue placeholder="Choose…" />
              </SelectTrigger>
              <SelectContent>
                {LEVELS.map((l) => (
                  <SelectItem key={l} value={l}>
                    {l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="referencing">Referencing style</Label>
            <Select value={referencing} onValueChange={setReferencing}>
              <SelectTrigger id="referencing" aria-label="Referencing style">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REFERENCING_STYLES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Optional — collapsed by default */}
        <button
          type="button"
          onClick={() => setShowMore((v) => !v)}
          className="mt-4 inline-flex items-center gap-1 text-xs font-mono tracking-wider text-primary hover:underline"
        >
          {showMore ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          {showMore ? "Hide" : "Add"} more context (optional)
        </button>
        {showMore && (
          <div className="mt-4 grid sm:grid-cols-2 gap-4 border border-border bg-paper/40 p-4">
            <div>
              <Label htmlFor="course">Course / programme</Label>
              <Input id="course" value={course} onChange={(e) => setCourse(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="module">Module</Label>
              <Input id="module" value={module} onChange={(e) => setModule(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="assessment-type">Assessment type</Label>
              <Input
                id="assessment-type"
                value={assessmentType}
                onChange={(e) => setAssessmentType(e.target.value)}
                placeholder="Essay, report, dissertation…"
              />
            </div>
            <div>
              <Label htmlFor="word-count">Word count target</Label>
              <Input
                id="word-count"
                value={wordCount}
                onChange={(e) => setWordCount(e.target.value)}
                placeholder="e.g. 2500"
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="rubric">Marking rubric / grade descriptors</Label>
              <Textarea
                id="rubric"
                rows={4}
                value={rubric}
                onChange={(e) => setRubric(e.target.value)}
              />
            </div>
          </div>
        )}
      </section>

      {/* ───────── Zone C: Consent + Generate ───────── */}
      <SectionRule className="mt-0 mb-8" />
      <section>
        <label className="flex items-start gap-3 text-sm cursor-pointer mb-5">
          <input
            type="checkbox"
            checked={consented}
            onChange={(e) => setConsented(e.target.checked)}
            className="mt-1"
          />
          <span className="text-ink-secondary">
            I confirm this is my own work, and I accept the{" "}
            <Link to="/terms" target="_blank" className="underline underline-offset-4 text-primary">
              Terms of Use
            </Link>{" "}
            including the AI-disclosure policy that may apply at my institution.
          </span>
        </label>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-ink-tertiary">
            You can fine-tune review depth, audience and bibliography audit after the first
            result.
          </p>
          <CTAButton
            variant="primary"
            onClick={() => canSubmit && submit.mutate()}
            disabled={!canSubmit}
          >
            {submit.isPending ? "Starting review…" : "Generate review"}
          </CTAButton>
        </div>
        {!canSubmit && !submit.isPending && (
          <p className="text-xs text-ink-tertiary mt-3">
            {!draft || draft.trim().length < 50
              ? "Add your draft to continue."
              : !institution?.name
                ? "Choose an institution to continue."
                : !level
                  ? "Choose your level to continue."
                  : !consented
                    ? "Tick the confirmation to continue."
                    : ""}
          </p>
        )}
      </section>
    </PageContainer>
  );
}
